import {
  DataExplorerSubscription,
  DataExplorerResource,
  DataExplorerTable,
  DataTreeResourceRequestProps,
  TablePreviewOpts,
  TilesetRequest,
  TilesetResourceStats,
  TableResourceStats,
  Dataset,
  DatasetVariables
} from '../types'
import { sendSQLPostRequest, sendSQLRequest } from 'features/sql/api/sql'
import { getRequest } from 'features/common'
import settings from 'settings'
import {
  buildQueryForDataTable,
  buildQueryForDeleteTable,
  buildQueryForSubscriptionDates
} from '../../common/queries/tableQueries'
import { Connection, ProviderType } from 'features/connections'
import { getClient } from 'features/common/utils/clientParameter'
import { encodeUriParams } from 'features/common/utils/searchParamsUtils'

export const PAGE_SIZE = 30

export const getDataExplorerObject = async ({
  link,
  signal
}: {
  link: string
  signal?: AbortSignal
}): Promise<DataExplorerTable | null> => {
  if (!link) {
    return null
  }
  const { workspaceApiUrl } = settings
  const resource: DataExplorerTable = await getRequest(`${workspaceApiUrl}${link}`, { signal })
  return resource
}

export const getObjectInfoLink = async ({
  link,
  page,
  search,
  refresh
}: {
  link: string
  page?: number
  search?: string
  refresh?: boolean
}): Promise<DataExplorerResource | null> => {
  if (!link) {
    return null
  }

  const url = `${settings.workspaceApiUrl}${link}?${encodeUriParams({
    pageSize: PAGE_SIZE,
    page: page || 1,
    search
  })}`

  const optsHeaders = {}
  if (refresh) {
    optsHeaders['Cache-Control'] = 'no-cache'
  }

  return getRequest(url, { headers: optsHeaders })
}

export const getDataTreeResource = async (
  { connectionId, projectId, datasetId, search }: Omit<DataTreeResourceRequestProps, 'targetKey'>,
  opts?: RequestInit
): Promise<DataExplorerResource> => {
  const url = `${settings.workspaceApiUrl}/connections/${connectionId}/resources/${
    datasetId || projectId || ''
  }?${encodeUriParams({
    pageSize: PAGE_SIZE,
    page: 1,
    search
  })}`

  return getRequest(url, opts)
}

export const getTableResourceStats = async ({
  connection,
  table
}: {
  connection: string
  table: string
}): Promise<TableResourceStats> => {
  const { workspaceApiUrl } = settings
  const url = `${workspaceApiUrl}/connections/${connection}/resource-stats/${table}`
  const response = await getRequest(url)
  return response
}

export const getSubscriptionResource = async (subscriptionId: string): Promise<DataExplorerSubscription> => {
  const { accountsApiUrl } = settings
  const url = `${accountsApiUrl}/do/subscriptions/${subscriptionId}`
  const resource: DataExplorerSubscription = await getRequest(url)
  return resource
}

export const getTilesetResourceStats = async ({
  connectionId,
  tilesetId
}: TilesetRequest): Promise<TilesetResourceStats> => {
  const { workspaceApiUrl } = settings
  const stats = await getRequest(`${workspaceApiUrl}/connections/${connectionId}/resource-stats/${tilesetId}`)
  return stats
}

export const getTablePreviewDataViaSQL = async ({
  connectionName,
  provider,
  table,
  schema,
  refresh
}: TablePreviewOpts) => {
  const query = buildQueryForDataTable({ provider, table, schema })
  const dataPreview = await sendSQLRequest({
    connection: connectionName,
    query,
    extraParams: { client: getClient() },
    refresh
  })
  return dataPreview
}

export const deleteResourcePostRequest = async ({ connection, table }: { connection: Connection; table: string }) => {
  return await sendSQLPostRequest({
    connection: connection.name,
    query: buildQueryForDeleteTable({ provider: connection.provider_id, table }),
    extraParams: { client: getClient() }
  })
}

export const getSubscriptionDates = async ({ connection, fqn }: { connection: string; fqn: string }) => {
  const query = buildQueryForSubscriptionDates(fqn)
  return await sendSQLRequest({ connection, query, extraParams: { client: getClient() } })
}

export const searchTableInConnection = async ({ connection, fqn }: { connection: Connection; fqn: string }) => {
  try {
    const tableName = fqn
    return await sendSQLPostRequest({
      connection: connection.name,
      ...getSearchTableQuery(tableName, connection),
      extraParams: { client: getClient() }
    })
  } catch (e) {
    return false
  }
}

const getSearchTableQuery = (table: string, connection: Connection) => {
  switch (connection.provider_id) {
    case ProviderType.SNOWFLAKE:
      return {
        query: `
        select system$explain_plan_json(
          'select * from ${table}'
        ) as explain_plan;
      `
      }
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return {
        query: `
        explain select * from ${table};
      `
      }
    case ProviderType.DATABRICKS_REST:
      return {
        query: `
          SELECT * FROM ${table} LIMIT 0
        `
      }
    case ProviderType.BIGQUERY:
      return {
        query: `
          SELECT * FROM \`${table}\` LIMIT 0
        `
      }
    case ProviderType.ORACLE:
      return {
        query: `
          SELECT * FROM ${table} WHERE ROWNUM <= 1
        `
      }
    default:
      throw new Error(`Provider ${connection.provider_id} is not supported yet`)
  }
}

export const getDatasetDetails = async (id: string): Promise<Dataset> => {
  const url = `${settings.spatialDataCatalogUrl}/do/catalog/datasets/${id}`
  const response = await fetch(url)
  return await response.json()
}

export const getDatasetVariables = async (id: string): Promise<DatasetVariables> => {
  const url = `${settings.spatialDataCatalogUrl}/do/catalog/datasets/${id}/data`
  const response = await fetch(url)
  return (await response.json())?.cols
}
